import React, { useState } from "react";
import moment from "moment";
import { MAX_SESSIONS_MONTH, MIN_SESSIONS_MONTH, SessionRow } from "../DeclarationCourse/SessionList";
import { rowsFromSelectedDates, toggleDate, toggleWeekday } from "../DeclarationCourse/sessionCalendarHelpers";
import { DV2 } from "../DeclarationV2/theme";
import { clock, hours, plural } from "../DeclarationV2/format";
import { vocab } from "../DeclarationV2/vocabulary";
import { Button, Card, Notice, SectionTitle, useDv2Theme } from "../DeclarationV2/layout/Ui";
import MonthCalendar from "../DeclarationV2/components/MonthCalendar";
import { DurationStepper, TimeSelect } from "../DeclarationV2/components/TimeFields";
import { endsNextDay, enforceEarliestStart, firstSelectableDay, timeToIso } from "../DeclarationV2/components/planningRules";

interface Props {
    /** Date de validation du compte d'avance immédiate de l'élève, si connue. */
    activationDate: string | null;
    /** Heure de début proposée (« HH:mm »), reprise du cours du mois dernier. */
    initialTime: string | null;
    /** Période du cours du mois dernier, rappelée au prof (« Du 1er au 20 octobre »). */
    previousPeriod: string;
    /** Total d'heures du cours du mois dernier, rappelé au prof. */
    previousHours: number;
    onGenerate: (rows: SessionRow[]) => void;
}

/**
 * Choix des jours d'un brouillon repris d'un ancien cours « Plusieurs cours »
 * sans détail des séances : le mois dernier n'a enregistré qu'une période et
 * une durée totale, pas les jours. Le prof coche ses jours du mois en cours,
 * choisit l'heure et la durée communes, puis génère les séances, comme à
 * l'étape « Quand ont lieu les cours ? » du nouveau parcours.
 */
const DraftDaysPicker = ({ activationDate, initialTime, previousPeriod, previousHours, onGenerate }: Props) => {
    const { fontFamily } = useDv2Theme();
    const minDate = firstSelectableDay(activationDate);
    const currentMonth = moment().format("YYYY-MM");
    const month = moment().startOf("month");

    const [selected, setSelected] = useState<string[]>([]);
    const [time, setTime] = useState<string | null>(initialTime);
    const [duration, setDuration] = useState<number>(1.5);
    const [limitReached, setLimitReached] = useState(false);

    const apply = (result: { dates: string[]; refused: boolean }) => {
        setSelected(result.dates);
        setLimitReached(result.refused);
    };
    const nextDayEnd = endsNextDay(time, duration);
    const canGenerate = selected.length >= MIN_SESSIONS_MONTH && !!time && !nextDayEnd;
    const generate = () => {
        if (!canGenerate) return;
        const rows = rowsFromSelectedDates(selected, [], { startTime: timeToIso(time!, selected[0]), duration });
        onGenerate(enforceEarliestStart(rows, activationDate));
    };

    return (
        <div className="flex flex-col gap-4" style={{ fontFamily }}>
            <Notice tone="info">
                Le mois dernier, ce {vocab.course()} a été déclaré avec l'ancienne déclaration globale : une période ({previousPeriod.charAt(0).toLowerCase()}{previousPeriod.slice(1)}, {hours(previousHours)} au total), sans le détail des jours. C'est pourquoi aucune séance n'est reprise ici.
                {" "}Cochez les jours de ce mois une seule fois : chaque séance sera enregistrée, et le mois prochain la Déclaration rapide vous les proposera directement.
            </Notice>
            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] gap-5 items-start">
                <Card style={{ display: "flex", flexDirection: "column", gap: 16, padding: 22 }}>
                    <div style={{ fontSize: 14, color: DV2.muted, lineHeight: 1.5 }}>
                        Cochez les dates de vos {vocab.courses()}. Un clic sur un jour de la semaine coche tous ces jours du mois.
                    </div>
                    <MonthCalendar
                        month={month}
                        onMonthChange={() => undefined}
                        firstMonth={currentMonth}
                        lastMonth={currentMonth}
                        selected={selected}
                        minDate={minDate}
                        onToggleDate={(date) => apply(toggleDate(selected, date, MAX_SESSIONS_MONTH))}
                        onToggleWeekday={(iso) => apply(toggleWeekday(selected, month, iso, MAX_SESSIONS_MONTH, minDate))}
                    />
                    <div style={{ fontSize: 13, color: DV2.muted }}>
                        <b style={{ color: DV2.text }}>{plural(selected.length, "date cochée", "dates cochées")}</b> sur {MAX_SESSIONS_MONTH} séances possibles{limitReached ? " : limite atteinte" : ""}
                    </div>
                    {nextDayEnd && (
                        <Notice tone="danger">
                            Avec ce début et cette durée, les séances se termineraient le lendemain à {nextDayEnd.replace(":", "h")}. Réduisez la durée ou avancez l'heure de début.
                        </Notice>
                    )}
                </Card>

                <div className="flex flex-col gap-3">
                    <Card style={{ display: "flex", flexDirection: "column", gap: 16, padding: 22 }}>
                        <SectionTitle>Même horaire et même durée pour toutes les séances</SectionTitle>
                        <div style={{ fontSize: 13, color: DV2.muted, lineHeight: 1.5, marginTop: -8 }}>
                            Vous pourrez ajuster l'heure et la durée de chaque séance ensuite.
                        </div>
                        <TimeSelect id="bv2-days-time" value={time} onChange={setTime} chips />
                        <DurationStepper id="bv2-days-duration" value={duration} onChange={setDuration} />
                        {time && (
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, borderTop: `1px solid ${DV2.borderSoft}`, paddingTop: 12 }}>
                                <span style={{ color: DV2.muted }}>Fin de séance</span>
                                <b>{clock(moment(timeToIso(time)).add(duration, "hours"))}</b>
                            </div>
                        )}
                    </Card>
                    <Button
                        label={`Générer ${selected.length >= 2 ? `les ${selected.length} séances` : "les séances"}`}
                        onClick={generate}
                        disabled={!canGenerate}
                        fullWidth
                    />
                    <div style={{ fontSize: 13, color: DV2.muted, textAlign: "center", lineHeight: 1.45 }}>
                        {selected.length < MIN_SESSIONS_MONTH
                            ? `Cochez au moins ${MIN_SESSIONS_MONTH} dates.`
                            : `${hours(duration * selected.length)} au total, contre ${hours(previousHours)} le mois dernier.`}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DraftDaysPicker;
